/* ==========================================================================
   ELEVEN v5 - global behaviour
   Lenis smooth scroll, anchor scrolling, masked display lines, scroll
   reveals, parallax and loop films (SITE.loops). Section scripts register with window.SITE.ready(fn).
   A device that asks for less motion (S.calm) keeps the reveals and loses the heavy effects: smooth scroll,
   parallax and the films. S.reduced, the page that stands completely still, is switched off (see head.html).
   ========================================================================== */
(function () {
  "use strict";
  var S = window.SITE;
  var reduced = S.reduced;
  var calm = !!S.calm;

  function whenLibs(cb) {
    if (document.readyState === "loading") document.addEventListener("DOMContentLoaded", cb, { once: true });
    else cb();
  }

  whenLibs(function () {
    var hasGsap = typeof window.gsap !== "undefined" && typeof window.ScrollTrigger !== "undefined";
    if (hasGsap) gsap.registerPlugin(ScrollTrigger);

    /* ---------- smooth scroll ---------- */
    if (!reduced && !calm && typeof window.Lenis !== "undefined") {
      // a jump to a section or to the top (scrollTo with a duration) starts and lands softly; the wheel keeps lerp
      var lenis = (window.lenis = new Lenis({ lerp: 0.1, smoothWheel: true, easing: function (t) { return t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2; } }));
      if (hasGsap) {
        lenis.on("scroll", ScrollTrigger.update);
        gsap.ticker.add(function (t) { lenis.raf(t * 1000); });
        gsap.ticker.lagSmoothing(0);
      } else {
        (function raf(t) { lenis.raf(t); requestAnimationFrame(raf); })(0);
      }
    }

    /* ---------- anchor links ---------- */
    document.addEventListener("click", function (e) {
      var a = e.target.closest && e.target.closest('a[href^="#"]');
      if (!a) return;
      var id = a.getAttribute("href");
      if (id.length < 2) return;
      var target = document.querySelector(id);
      if (!target) return;
      e.preventDefault();
      if (window.lenis) window.lenis.scrollTo(target, { offset: 0, duration: 1.3 });
      else target.scrollIntoView({ behavior: reduced || calm ? "auto" : "smooth" });
      if (!target.hasAttribute("tabindex")) target.setAttribute("tabindex", "-1");
      target.focus({ preventScroll: true });
    });

    /* ---------- typesetting ---------- */
    // A one-letter word or a plain number never ends a line: it is glued to the next
    // word with a no-break space, and so is a currency ("EUR 27", "50 tys. zł"). English glues
    // "a" and "I"; the Polish page (<html lang="pl">) glues a, i, o, u, w and z.
    // A number compound such as "15-minute" never breaks at its hyphen (word joiner
    // after the hyphen). Runs on the copy leaves at runtime, before the line
    // splitter, so the sources and content.json stay plain text.
    var GLUE = /^pl\b/i.test(document.documentElement.lang || "")
      ? /(^|[\s ])([aiouwzAIOUWZ]|\d+)[ \t\n\r]+(?=\S)/g
      : /(^|[\s ])([aAI]|\d+)[ \t\n\r]+(?=\S)/g;
    function typeset(el) {
      if (el.children.length) return;
      var t = el.textContent, u = t;
      for (var n = 0; n < 2; n++) u = u.replace(GLUE, "$1$2 ");
      u = u.replace(/\b(PLN|EUR)[ \t\n\r]+(?=\d)/g, "$1 ");
      u = u.replace(/(tys\.|mln|mld)[ \t\n\r]+(?=zł)/g, "$1 ");
      u = u.replace(/(\d)-(?=[A-Za-z])/g, "$1-⁠");
      if (u !== t) el.textContent = u;
    }

    /* ---------- masked display lines ---------- */
    function splitLines(el) {
      if (el.dataset.split === "done") return;
      var text = el.textContent.replace(/[ \t\n\r]+/g, " ").trim();
      el.setAttribute("aria-label", text.replace(/ /g, " ").replace(/⁠/g, ""));
      var words = text.split(" ");
      el.textContent = "";
      var probes = words.map(function (w, i) {
        var s = document.createElement("span");
        s.textContent = w + (i < words.length - 1 ? " " : "");
        s.style.display = "inline-block";
        s.style.whiteSpace = "pre";
        el.appendChild(s);
        return s;
      });
      var lines = [], lastTop = null;
      probes.forEach(function (s) {
        var top = s.offsetTop;
        if (lastTop === null || Math.abs(top - lastTop) > 2) { lines.push([]); lastTop = top; }
        lines[lines.length - 1].push(s.textContent);
      });
      el.textContent = "";
      lines.forEach(function (ws) {
        var mask = document.createElement("span");
        mask.className = "line-mask";
        mask.setAttribute("aria-hidden", "true");
        var inner = document.createElement("span");
        inner.className = "line-inner";
        inner.textContent = ws.join("").replace(/[ \t\n\r]+$/, "");
        mask.appendChild(inner);
        el.appendChild(mask);
      });
      el.dataset.split = "done";
    }

    /* ---------- loop films ---------- */
    // A still that carries data-loop="<mp4>" (and optionally data-loop-s="<smaller mp4>") gets a silent looping
    // film laid exactly over it, on computers only (Jan, 2026-10-04: films play on a computer and stay still on
    // a phone). The film is created when the still comes on screen, plays only while it is on screen and the tab
    // is visible, and is dropped if it cannot load or play. A still inside [data-loop-paused] never plays:
    // sections put that attribute on inactive tabs or slides and then call SITE.loops.refresh(). A still that
    // is visibility: hidden (a card the hero's curved wall has culled) does not play either; the hero calls
    // refresh() when a card comes into view or leaves it.
    S.loops = (function () {
      var conn = navigator.connection || {};
      var can = !reduced && !calm && !conn.saveData && "IntersectionObserver" in window && typeof WeakMap !== "undefined" &&
        window.matchMedia("(hover: hover) and (pointer: fine)").matches && window.innerWidth >= 1024;
      var items = [], byImg = can ? new WeakMap() : null, MAX = 6;
      function playing() { return items.filter(function (it) { return it.on; }).length; }
      function pick(img) {
        var large = img.getAttribute("data-loop"), small = img.getAttribute("data-loop-s");
        if (!small) return large;
        var portrait = img.offsetHeight > img.offsetWidth;
        var px = img.offsetWidth * Math.min(window.devicePixelRatio || 1, 2);
        return px <= (portrait ? 360 : 768) * 1.2 ? small : large;
      }
      function place(it) {
        var img = it.img, v = it.video, cs = getComputedStyle(img);
        v.style.left = img.offsetLeft + "px"; v.style.top = img.offsetTop + "px";
        v.style.width = img.offsetWidth + "px"; v.style.height = img.offsetHeight + "px";
        v.style.objectFit = cs.objectFit; v.style.objectPosition = cs.objectPosition; v.style.borderRadius = cs.borderRadius;
      }
      function drop(it) {
        var v = it.video; it.on = false; it.video = null;
        if (v) { v.pause(); v.removeAttribute("src"); v.load(); if (v.parentNode) v.parentNode.removeChild(v); }
      }
      function build(it) {
        var v = document.createElement("video"), parent = it.img.parentNode;
        v.className = "loop-film"; v.muted = true; v.loop = true; v.playsInline = true; v.preload = "auto";
        v.setAttribute("muted", ""); v.setAttribute("playsinline", ""); v.setAttribute("aria-hidden", "true");
        v.setAttribute("tabindex", "-1"); v.setAttribute("disableremoteplayback", ""); v.disablePictureInPicture = true;
        v.addEventListener("playing", function () { v.classList.add("is-on"); });
        v.addEventListener("error", function () { it.dead = true; drop(it); });
        v.src = pick(it.img);
        if (getComputedStyle(parent).position === "static") parent.style.position = "relative";
        parent.insertBefore(v, it.img.nextSibling);
        it.video = v; place(it);
      }
      function sync(it) {
        var want = it.seen && !it.dead && !document.hidden && !it.img.closest("[data-loop-paused]") &&
          getComputedStyle(it.img).visibility !== "hidden";
        if (want) {
          if (!it.video) { if (playing() >= MAX) return; build(it); }
          if (!it.on) {
            it.on = true;
            var p = it.video.play();
            if (p && p.catch) p.catch(function (err) {
              if (err && err.name === "AbortError") { it.on = false; return; }   // paused again before it started
              it.dead = true; drop(it);
            });
          }
        } else if (it.on) { it.on = false; it.video.pause(); }
      }
      var io = can ? new IntersectionObserver(function (entries) {
        entries.forEach(function (e) { var it = byImg.get(e.target); if (it) it.seen = e.isIntersecting && e.intersectionRatio >= 0.2; });
        items.forEach(sync);
      }, { threshold: [0, 0.2, 0.6] }) : null;
      function refresh() {
        if (!can) return;
        Array.prototype.forEach.call(document.querySelectorAll("img[data-loop]"), function (img) {
          if (byImg.has(img)) return;
          var it = { img: img, video: null, on: false, seen: false, dead: false };
          byImg.set(img, it); items.push(it); io.observe(img);
        });
        items.forEach(function (it) { if (it.video) place(it); sync(it); });
      }
      if (can) {
        document.addEventListener("visibilitychange", function () { items.forEach(sync); });
        window.addEventListener("resize", function () { items.forEach(function (it) { if (it.video) place(it); }); });
      }
      return { enabled: can, refresh: refresh };
    })();

    // The offer's wording needs no fonts: it is written as soon as the page has been read, so the bar at the top, the
    // header's percent and the sale bar show their words at once instead of after the fonts (up to 2.5 s on a slow
    // phone; review, 2026-10-15). data-offer-words lets those three show; the rest still waits for data-offer-wait.
      // The offer as the host knows it (the Shopify theme, from its product): window.ELEVEN_OFFER = { price, regular,
      // plans }. With a price above zero the pages read as the paid launch sale: a text that differs carries its paid
      // wording in data-paid ({price}, {regular}, {save}, {pct}, {max} and {plans} are filled in), data-paid-many,
      // -one and -none replace the wordings that follow the spots, and what is marked data-mode="paid" shows.
      // Without it, or at a price of zero, the pages stay as they are written: the free launch spot.
      (function () {
        var o = window.ELEVEN_OFFER;
        if (!o || !(o.price > 0)) return;
        var money = function (n) { var p = (Math.round(n * 100) % 100 ? n.toFixed(2) : String(Math.round(n))).split("."); return "$" + p[0].replace(/\B(?=(\d{3})+(?!\d))/g, ",") + (p[1] ? "." + p[1] : ""); };
        var plans = o.plans && o.plans.length ? o.plans.slice(0, 6) : [o.price];
        var sale = o.regular > o.price;   // a price at or above the regular price is no sale: nothing speaks of a saving
        var tok = {
          price: money(o.price), regular: o.regular ? money(o.regular) : "", save: sale ? money(o.regular - o.price) : "",
          // rounded down, so the percent never promises more than the saving is ($99 of $3,999 is 97.5%: "97%")
          pct: sale ? Math.floor((1 - o.price / o.regular) * 100) + "%" : "", max: String(plans.length),
          plans: plans.slice(1).map(function (a, i) { return (i + 2) + " x " + money(a); }).join(", "),
        };
        var single = plans.length < 2;   // a price, no payment plan
        var fill = function (t) { return t.replace(/\{(\w+)\}/g, function (m, k) { return tok[k] !== undefined ? tok[k] : m; }); };
        var all = function (sel, fn) { Array.prototype.forEach.call(document.querySelectorAll(sel), fn); };
        // the paid wording of one element; the host calls it again for a text it changed (S.paidText)
        var paidText = function (el) {
          if (!el.hasAttribute("data-paid")) return;
          el.textContent = fill(single && el.hasAttribute("data-paid-single") ? el.getAttribute("data-paid-single") : el.getAttribute("data-paid"));
          ["many", "one", "none"].forEach(function (k) {
            if (el.hasAttribute("data-paid-" + k)) el.setAttribute("data-spots-" + k, fill(el.getAttribute("data-paid-" + k)));
            else el.removeAttribute("data-spots-" + k);
          });
          if (el.hasAttribute("data-paid-one") && el.hasAttribute("data-one")) el.setAttribute("data-one", fill(el.getAttribute("data-paid-one")));
        };
        all("[data-paid]", paidText);
        S.paidText = paidText;
        // items of the paid sale, and those that only make sense with a payment plan
        all('[data-mode="paid"], [data-mode="plan"]', function (el) { el.hidden = el.getAttribute("data-mode") === "plan" && single; });
        document.documentElement.setAttribute("data-offer", "paid");
        if (single) document.documentElement.setAttribute("data-offer-single", "");   // one payment, no plan (the phone ticket is shorter then)
        // what is marked data-sale (the percent, the saving, the crossed-out price) only shows while there is a saving
        if (!sale) document.documentElement.setAttribute("data-nosale", "");
      })();
      if (S.applySpots) S.applySpots();
      document.documentElement.setAttribute("data-offer-words", "");

    function start() {
      if (S.applySpots) S.applySpots();   // the launch spots left, before any script reads the page
      Array.prototype.forEach.call(document.querySelectorAll("[data-content]"), typeset);
      S.typeset = typeset;                // for a text that changes later (the spots, when the store reports a newer number)
      var lineEls = Array.prototype.slice.call(document.querySelectorAll("[data-lines]"));
      lineEls.forEach(splitLines);

      S._ready.forEach(function (fn) { try { fn(); } catch (err) { console.error("[SITE] section hook failed", err); } });
      // a priced offer: its wording and its prices are written now, so what waited for them shows (see the layout of the store)
      document.documentElement.removeAttribute("data-offer-wait");
      S.loops.refresh();

      if (hasGsap && !reduced) {
        // Play once the top of the element rises above the lower 10% of the screen. Anything already on screen plays
        // at load. Where an element is, is asked while the page scrolls, not measured once: positions measured once
        // go stale when a phone is turned, a picture loads late or a section changes height, and a reveal waiting
        // at a place that no longer exists never plays (after a turn of the phone the footer stayed invisible).
        // Only the height on the screen counts, so the cards of a sideways row all play when their row comes up.
        var pending = [], asked = 0;
        var look = function () {
          asked = 0;
          var h = window.innerHeight, line = h * 0.9;
          // the last things on a page may never rise above the line: at the end of the page, whatever is on screen plays
          var end = h + window.pageYOffset >= document.documentElement.scrollHeight - 4;
          var due = pending.filter(function (p) { var top = p.el.getBoundingClientRect().top; return top < line || (end && top < h); });
          if (!due.length) return;
          pending = pending.filter(function (p) { return due.indexOf(p) < 0; });
          due.forEach(function (p) { try { p.play(); } catch (err) { console.error("[SITE] reveal failed", err); } });
          if (!pending.length) { window.removeEventListener("scroll", ask); window.removeEventListener("resize", ask); ScrollTrigger.removeEventListener("refresh", ask); }
        };
        var ask = function () { if (!asked) asked = requestAnimationFrame(look); };
        var whenSeen = function (el, play) {
          var r = el.getBoundingClientRect();
          if (r.top < window.innerHeight * 0.9 && r.bottom > 0) { play(); return; }
          pending.push({ el: el, play: play });
        };
        window.addEventListener("scroll", ask, { passive: true });
        window.addEventListener("resize", ask);
        // a section that changes height (an answer closes, the quiz moves on) says so with ScrollTrigger.refresh():
        // what it moved up into view plays then, without waiting for the next scroll
        ScrollTrigger.addEventListener("refresh", ask);

        lineEls.forEach(function (el) {
          var inners = el.querySelectorAll(".line-inner");
          gsap.set(inners, { yPercent: 120 });
          whenSeen(el, function () {
            gsap.to(inners, { yPercent: 0, duration: 1.1, ease: "power3.out", stagger: 0.08, delay: parseFloat(el.getAttribute("data-delay") || "0") });
          });
        });

        gsap.utils.toArray("[data-reveal]").forEach(function (el) {
          var kind = el.getAttribute("data-reveal");
          var delay = parseFloat(el.getAttribute("data-delay") || "0");
          var from = kind === "fade" ? { opacity: 0 } : kind === "scale" ? { opacity: 0, scale: 0.98 } : { opacity: 0, y: 16 };
          var to = { opacity: 1, y: 0, scale: 1, duration: kind === "fade" ? 1.1 : 1, ease: "power3.out", delay: delay, paused: true };
          var tween = gsap.fromTo(el, from, to);
          whenSeen(el, function () { tween.play(); });
        });

        if (!calm) gsap.utils.toArray("[data-parallax]").forEach(function (el) {
          var f = parseFloat(el.getAttribute("data-parallax")) || 0.1;
          gsap.fromTo(el, { y: function () { return window.innerHeight * f * 0.5; } }, {
            y: function () { return -window.innerHeight * f * 0.5; }, ease: "none",
            scrollTrigger: { trigger: el, start: "top bottom", end: "bottom top", scrub: true, invalidateOnRefresh: true },
          });
        });
      } else {
        document.querySelectorAll("[data-reveal]").forEach(function (el) { el.style.opacity = 1; el.style.transform = "none"; });
      }

      document.documentElement.classList.add("is-loaded");
      if (hasGsap) ScrollTrigger.refresh();
    }

    // Lines are split by measuring text, so the fonts come first. They are files of this site and
    // arrive at once; should one hang, the page starts without it after 2.5 s rather than stay hidden.
    var started = false;
    function startOnce() { if (started) return; started = true; start(); }
    if (document.fonts && document.fonts.ready) { document.fonts.ready.then(startOnce); window.setTimeout(startOnce, 2500); }
    else startOnce();
    window.addEventListener("load", function () { if (hasGsap) ScrollTrigger.refresh(); });

    // Pictures that are not on show at first (the other slides of a switcher, marked data-src) and the hero cards
    // further along the row are fetched once the page has loaded, or at the first touch: they do not compete
    // with the first screen, and they are there before anyone gets to them.
    var later = false;
    function fetchLater() {
      if (later) return; later = true;
      // (the srcset first, so the browser picks the file for the screen; the hero's three far cards wait like this too,
      // 2026-10-15: a phone met them only after about 45 s of the wall's drift, and they took 152 KB from its first screen)
      Array.prototype.forEach.call(document.querySelectorAll("img[data-src]"), function (img) { if (img.hasAttribute("data-srcset")) { img.srcset = img.getAttribute("data-srcset"); img.removeAttribute("data-srcset"); } img.src = img.getAttribute("data-src"); img.removeAttribute("data-src"); });
      Array.prototype.forEach.call(document.querySelectorAll('#hero .hero-card:not([data-dup]) img[loading="lazy"]'), function (img) { img.loading = "eager"; });
      if (S.loops) S.loops.refresh();
    }
    window.addEventListener("load", function () { window.setTimeout(fetchLater, 1200); });
    // (a key press too; not the first touch: on a phone that is the first scroll, and it started 300 KB of hidden slides
    // while the first screen was still loading, 2026-10-15)
    window.addEventListener("keydown", fetchLater, { once: true, capture: true });
  });
})();
